import {PlusIcon} from '@heroicons/react/outline';
import type {JSX} from 'react';
import {useState} from 'react';

import {buildDownloadFileName} from '@afriex/core/utils';

import {Button} from '@/components';
import {transactionService} from '@/services/transactionService';
import type {AfriexQueryParams} from '@/types';
import {saveBlob} from '@/utils/downloadFile';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';
import {showErrorMessage} from '@/utils/showErrorMessage';

interface DownloadTransactionCSVButtonProps {
  /** The filters the list on screen was fetched with. */
  filters: AfriexQueryParams;
  /** Set on a user's own transaction list, which exports only that user. */
  userId?: string;
  /** True when the list on screen is empty, so there is nothing to export. */
  disabled: boolean;
}

/**
 * Downloads every transaction matching the list's filters, not just the page
 * on screen: the server streams the CSV (see adminTransactionCsv.ts) and this
 * only saves it.
 */
const DownloadTransactionCSVButton = ({
  filters,
  userId,
  disabled,
}: DownloadTransactionCSVButtonProps): JSX.Element => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const blob = userId
        ? await transactionService.downloadUserTransactionList(userId, filters)
        : await transactionService.downloadTransactionList(filters);
      saveBlob(blob, buildDownloadFileName('transactions', 'csv', userId));
    } catch (error) {
      showErrorMessage(getErrorMessage(error, 'Failed to export transactions'));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      colorScheme="cyan"
      leftIcon={<PlusIcon className="h-5 w-5" />}
      disabled={disabled || isDownloading}
      isLoading={isDownloading}
      loadingText="Exporting"
      onClick={() => {
        // handleDownload reports its own failures, so this only fires if that
        // reporting itself throws.
        handleDownload().catch(logRejection('Failed to export transactions'));
      }}
      data-cy="admin-transactions-export-link"
    >
      Export as .CSV
    </Button>
  );
};

export default DownloadTransactionCSVButton;
